'use client';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { register } from '@/lib/api/auth';
import { ApiError } from '@/lib/api/client';
import { Eye, EyeOff } from 'lucide-react';
import { MicrosoftIcon, GoogleIcon } from '@/components/icons/social-icons';

type Translate = (key: string) => string;

function makeRegisterSchema(tAuth: Translate, t: Translate) {
  return z
    .object({
      email: z.email(tAuth('invalidEmail')),
      password: z.string().min(12, t('passwordMin')),
      confirmPassword: z.string(),
      firstName: z.string().min(1, t('firstNameRequired')),
      lastName: z.string().min(1, t('lastNameRequired')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('passwordMismatch'),
      path: ['confirmPassword'],
    });
}

type RegisterFormData = z.infer<ReturnType<typeof makeRegisterSchema>>;

/**
 * Fixed English error codes produced by our own code rather than the backend:
 * app/api/register/route.ts ('Payload too large', 'Invalid request body') and
 * the register() fallback in lib/api/auth.ts ('Registration failed').
 */
const LOCAL_ERROR_CODES = new Set([
  'Registration failed',
  'Payload too large',
  'Invalid request body',
]);

/** User-facing message for a register() failure. */
function registerErrorMessage(err: unknown, t: Translate, tAuth: Translate): string {
  if (err instanceof ApiError) {
    // 5xx: the route's 'Bad Gateway' (backend unreachable / non-JSON response).
    if (err.status >= 500) return tAuth('serverError');
    if (LOCAL_ERROR_CODES.has(err.message)) return t('failed');
  }
  // Anything else is the backend's own (validation) message — shown as-is.
  return err instanceof Error && err.message ? err.message : t('failed');
}

interface RegisterFormProps {
  microsoftEnabled?: boolean;
  googleEnabled?: boolean;
}

export function RegisterForm({
  microsoftEnabled = false,
  googleEnabled = false,
}: RegisterFormProps) {
  const t = useTranslations('register');
  const tAuth = useTranslations('auth');
  const registerSchema = useMemo(() => makeRegisterSchema(tAuth, t), [tAuth, t]);
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSocialLoading, setIsSocialLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register: registerField,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      // Register the user
      const response = await register({
        email: data.email,
        password: data.password,
        first_name: data.firstName,
        last_name: data.lastName,
      });

      // Auto sign-in with the returned tokens — use the tokens from the
      // registration response directly to avoid a second round-trip to
      // /api/v1/auth/token/ (which would fail if email verification is required).
      const result = await signIn('credentials', {
        email: data.email,
        accessToken: response.tokens.access,
        refreshToken: response.tokens.refresh,
        redirect: false,
      });

      if (result?.error) {
        // Registration succeeded but login failed - go to login page
        router.push('/login?registered=true');
      } else {
        // Redirect to onboarding since new users always need it
        router.push('/onboarding');
      }
    } catch (err) {
      setError(registerErrorMessage(err, t, tAuth));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = (provider: 'azure-ad' | 'google') => {
    setIsSocialLoading(true);
    setError(null);
    signIn(provider, { callbackUrl: '/dashboard' });
  };

  const isDisabled = isLoading || isSocialLoading;

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="text-xl">{t('title')}</CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          {error && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500">
              {error}
            </div>
          )}

          {(microsoftEnabled || googleEnabled) && (
            <>
              <div
                className={`grid gap-3 w-full ${microsoftEnabled && googleEnabled ? 'grid-cols-2' : 'grid-cols-1'}`}
              >
                {microsoftEnabled && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleSocialLogin('azure-ad')}
                    disabled={isDisabled}
                  >
                    <MicrosoftIcon className="mr-2 h-4 w-4" />
                    Microsoft
                  </Button>
                )}
                {googleEnabled && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleSocialLogin('google')}
                    disabled={isDisabled}
                  >
                    <GoogleIcon className="mr-2 h-4 w-4" />
                    Google
                  </Button>
                )}
              </div>

              <div className="relative w-full">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">
                    {t('orRegisterWithEmail')}
                  </span>
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">{tAuth('firstName')}</Label>
              <Input
                id="firstName"
                placeholder={tAuth('firstName')}
                {...registerField('firstName')}
              />
              {errors.firstName && (
                <p className="text-sm text-red-500">{errors.firstName.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">{tAuth('lastName')}</Label>
              <Input id="lastName" placeholder={tAuth('lastName')} {...registerField('lastName')} />
              {errors.lastName && <p className="text-sm text-red-500">{errors.lastName.message}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{tAuth('email')}</Label>
            <Input
              id="email"
              type="email"
              placeholder={tAuth('emailPlaceholder')}
              {...registerField('email')}
            />
            {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{tAuth('password')}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={t('passwordPlaceholder')}
                {...registerField('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? t('hidePassword') : t('showPassword')}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="text-sm text-red-500">{errors.password.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                {...registerField('confirmPassword')}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showConfirmPassword ? t('hidePassword') : t('showPassword')}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-red-500">{errors.confirmPassword.message}</p>
            )}
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-4">
          <Button type="submit" className="w-full" disabled={isDisabled}>
            {isLoading ? t('submitting') : t('submit')}
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            {t('haveAccount')}{' '}
            <Link href="/login" className="text-primary hover:underline">
              {tAuth('signIn')}
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}
